'use client';

import { ArrowForward } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  InputAdornment,
  InputBase,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import React, { ChangeEvent, useMemo, useState } from 'react';

import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { GuaranteeIcon } from '@/components/Icons/GuaranteeIcon';
import { trackEvent } from '@/lib/analytics/gtm';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { buildUrlWithAmount, formatNumberWithCommas } from '@/lib/helpers/formatHelpers';
import { getImageUrl } from '@/lib/helpers/getImageUrl';
import { getPageCodeFromPath } from '@/lib/helpers/pageHelpers';
import { buildUrlWithUtmParams } from '@/lib/helpers/utmParamHelpers';
import { gradientTextCss } from '@/theme';
import {
  ComponentBlocksFunnel,
  Enum_Componentblocksfunnel_Funnelvariant,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

type FunnelLeadCaptureProps = {
  block: ComponentBlocksFunnel;
  inPopup?: boolean;
  wrapperProps?: React.ComponentProps<typeof Box>;
};

export function FunnelLeadCapture({
  block,
  wrapperProps,
  inPopup = false,
}: FunnelLeadCaptureProps) {
  const theme = useTheme();
  const pathname = usePathname();
  const {
    FunnelLogo,
    FunnelHeader,
    FunnelSubHeader,
    InputLabel,
    InputPlaceholder,
    InputLink,
    FunnelVariant = Enum_Componentblocksfunnel_Funnelvariant.Popup,
    FunnelContents,
    FunnelFootNote,
    FunnelHideInnerBackground = false,
  } = block;

  const [amount, setAmount] = useState<string>(InputPlaceholder ?? '50,000');

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/,/g, '').replace(/[^0-9.]/g, '');
    const parts = input.split('.');

    if (parts.length > 2 || parts[1]?.length > 2) return;

    const formatted = formatNumberWithCommas(input);
    setAmount(formatted);
  };

  const linkUrl = useMemo(() => {
    const currentPageCode = getPageCodeFromPath(pathname);
    const baseUrl = buildUrlWithAmount(InputLink, amount.replace(/,/g, ''));
    return buildUrlWithUtmParams(baseUrl, `${currentPageCode}${inPopup ? '_popup' : ''}`);
  }, [InputLink, amount, pathname, inPopup]);

  const isNewHomepage = FunnelVariant === Enum_Componentblocksfunnel_Funnelvariant.NewHomepage;

  if (isNewHomepage) {
    return (
      <CustomBackground
        backgroundOptions={(block?.FunnelBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          (block?.FunnelDisplayOptions ?? {
            ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: 'md',
            PaddingBottom: 'md',
          }) as StrapiDisplayOptions | undefined
        }
        boxProps={{
          // @ts-expect-error - actually does support data props
          'data-component': 'FunnelLeadCapture-NewHomepage',
          sx: { textAlign: 'center' },
          ...wrapperProps,
        }}
      >
        <Box
          sx={{
            maxWidth: 760,
            mx: 'auto',
            px: { xs: 2, md: 0 },
            py: { xs: 2, md: 3 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Logo */}
          {getImageUrl({ image: FunnelLogo, size: 'url' }) && (
            <Box mb={4}>
              <Image
                src={getImageUrl({ image: FunnelLogo, size: 'url' })!}
                alt={FunnelLogo?.alternativeText || 'Logo'}
                width={FunnelLogo?.width || 150}
                height={FunnelLogo?.height || 40}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>
          )}

          {/* Rich text body / fallback header */}
          {FunnelContents && FunnelContents.length > 0 ? (
            <GlobalBlockRenderer content={FunnelContents} />
          ) : (
            <>
              {FunnelHeader && (
                <Typography
                  variant="subtitle1"
                  sx={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.4, mb: 2 }}
                >
                  {FunnelHeader}
                </Typography>
              )}
              {FunnelSubHeader && (
                <Typography variant="h5" sx={{ mt: 1, mb: 3 }}>
                  {FunnelSubHeader}
                </Typography>
              )}
            </>
          )}

          {/* Amount input + button */}
          <Box sx={{ maxWidth: 440, mx: 'auto', width: '100%', textAlign: 'left' }}>
            {InputLabel && (
              <Typography
                component="label"
                htmlFor={`funnel-amount-${block.id}`}
                sx={{
                  display: 'block',
                  mb: 1.3,
                  fontSize: { xs: 16, md: 18 },
                  fontWeight: 800,
                  color: theme.palette.common.white,
                  '.background-none &': { color: theme.palette.brand.bodyText },
                }}
              >
                {InputLabel}
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                gap: { xs: 1.2, sm: 0 },
              }}
            >
              <InputBase
                id={`funnel-amount-${block.id}`}
                value={amount}
                onChange={handleChange}
                inputProps={{
                  inputMode: 'decimal',
                  pattern: '[0-9.]*',
                  'aria-label': 'Loan amount',
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <Box
                      component="img"
                      src="/assets/new-homepage/search-loan-dollar.svg"
                      alt=""
                      aria-hidden="true"
                      sx={{ width: 32, height: 32, display: 'block' }}
                    />
                  </InputAdornment>
                }
                sx={{
                  flex: { xs: '1 1 auto', sm: '0 0 260px' },
                  height: 52,
                  pl: 1.3,
                  pr: 2,
                  background: theme.palette.common.white,
                  borderRadius: { xs: 1.5, sm: '12px 0 0 12px' },
                  color: theme.palette.brand.bodyText,
                  fontSize: 18,
                  fontWeight: 800,
                  '& input': { p: 0 },
                }}
              />
              <Button
                href={linkUrl}
                target="_blank"
                rel="nofollow noopener"
                variant="contained"
                endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
                onClick={() =>
                  trackEvent({ feature: 'funnel', action: 'funnel_cta_click', isKeyEvent: true })
                }
                sx={{
                  height: 52,
                  px: { xs: 3, sm: 3.5 },
                  minWidth: { sm: 150 },
                  borderRadius: { xs: 999, sm: '999px' },
                  background: theme.palette.brand.bodyText,
                  color: theme.palette.common.white,
                  fontSize: 13,
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  ml: { sm: -3 },
                  zIndex: 1,
                  '&:hover,&:focus': {
                    background: '#151023',
                    boxShadow: 'none',
                  },
                }}
              >
                {block?.FunnelButtonLabel ?? 'Get Best Loan'}
              </Button>
            </Box>

            {FunnelFootNote && (
              <Typography
                sx={{
                  mt: 1.6,
                  fontSize: 12,
                  color: alpha(theme.palette.common.white, 0.78),
                  '.background-none &': { color: theme.palette.brand.bodyText },
                }}
              >
                {FunnelFootNote}
              </Typography>
            )}
          </Box>
        </Box>
      </CustomBackground>
    );
  }

  return (
    <CustomBackground
      backgroundOptions={(block?.FunnelBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        (block?.FunnelDisplayOptions ?? {
          ContentsWidth: Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: 'md',
          PaddingBottom: 'md',
          CenterAlignTitles: true,
          CenterAlignParagraphs: true,
        }) as StrapiDisplayOptions | undefined
      }
      boxProps={{
        // @ts-expect-error - actually does support data props
        'data-component': 'FunnelLeadCapture',
        sx: {
          textAlign: 'center',
        },
        ...wrapperProps,
      }}
    >
      <Box className={`funnel-container funnel-container-${FunnelVariant}`}>
        <Box
          sx={{
            borderRadius: 50,
            background:
              FunnelHideInnerBackground === true
                ? 'none'
                : `radial-gradient(circle at 50% 50%, ${alpha(
                    theme.palette.common.white,
                    0.9
                  )} 65%, ${alpha(theme.palette.common.white, 0.6)} 100%)`,
            paddingTop: FunnelHideInnerBackground === true ? 0 : 0.5,
            '.funnel-container-Popup &': {
              px: { xs: 2, sm: 6 },
            },
          }}
        >
          {/* Logo */}
          {getImageUrl({ image: FunnelLogo, size: 'url' }) && (
            <Box mb={4}>
              <Image
                src={getImageUrl({ image: FunnelLogo, size: 'url' })!}
                alt={FunnelLogo?.alternativeText || 'Logo'}
                width={FunnelLogo?.width || 150}
                height={FunnelLogo?.height || 40}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>
          )}

          {/* Header */}
          {(!FunnelContents || FunnelContents?.length === 0) && FunnelHeader && (
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 700,
                px: 3,
                lineHeight: 1.4,
                mb: 2,
              }}
            >
              {FunnelHeader}
            </Typography>
          )}

          {/* Subheader */}
          {(!FunnelContents || FunnelContents?.length === 0) && FunnelSubHeader && (
            <Typography variant="h5" sx={{ mt: 1, mb: 3 }}>
              {FunnelSubHeader}
            </Typography>
          )}

          {FunnelContents && FunnelContents?.length > 0 && (
            <Box>
              <GlobalBlockRenderer content={FunnelContents || []} />
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '30rem',
              mx: 'auto',
            }}
          >
            {/* Input label */}
            {InputLabel && (
              <Typography
                variant="caption"
                sx={{
                  py: 0,
                  textAlign: 'left',
                  display: 'inline-block',
                  '&.funnel-input-Popup': {
                    fontSize: theme.typography.medium.fontSize,
                    fontWeight: 600,
                  },
                }}
              >
                {InputLabel}
              </Typography>
            )}

            {/* Input + Button */}
            <Box
              className={`funnel-input funnel-input-${FunnelVariant}`}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 },
                mt: 1,
                mx: 'auto',
                width: '100%',
                maxWidth: { xs: '100%', sm: '30rem' },
              }}
            >
              {/* Rounded Input */}
              <Box
                sx={{
                  flex: { xs: '0 0 100%', sm: 1 },
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: { xs: '100%', sm: '20rem' },
                }}
              >
                <TextField
                  fullWidth
                  placeholder="Enter Amount"
                  value={amount}
                  onChange={handleChange}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            background: theme.palette.purple[600],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.common.white,
                              fontSize: '0.95rem',
                              textAlign: 'center',
                            }}
                          >
                            $
                          </Typography>
                        </Box>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {amount && (
                          <Typography
                            sx={{
                              color: theme.palette.grey[500],
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: '1rem',
                            }}
                            onClick={() => setAmount('')}
                          >
                            ×
                          </Typography>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '.MuiOutlinedInput-root': {
                      height: { xs: '3rem', sm: '3.2rem' },
                      borderRadius: '10px',
                      border: `0.15rem solid ${theme.palette.purple[400]}`,
                      backgroundColor: theme.palette.common.white,
                      '& fieldset': { border: 'none' },
                      '&:hover': {
                        borderColor: theme.palette.purple[600],
                      },
                    },
                    input: {
                      fontSize: { xs: '1rem', sm: '1.05rem' },
                      fontWeight: 600,
                      paddingLeft: 0,
                    },
                  }}
                />
              </Box>

              {/* Gradient CTA Button */}
              <Box sx={{ flexShrink: 0 }}>
                <Button
                  href={linkUrl}
                  target="_blank"
                  rel="nofollow noopener"
                  variant="contained"
                  color="primary"
                  aria-disabled={!amount}
                  endIcon={<ArrowForward />}
                  className="getting-started-button"
                  onClick={() =>
                    trackEvent({ feature: 'funnel', action: 'funnel_cta_click', isKeyEvent: true })
                  }
                  sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: { xs: 1, sm: 1.25 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    height: { xs: '3rem', sm: '3.2rem' },
                    borderRadius: '999px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {block?.FunnelButtonLabel ?? `Get started`}
                </Button>
              </Box>
            </Box>
          </Box>
          <Typography
            component="p"
            variant="caption"
            sx={{
              ...gradientTextCss(theme),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              fontSize: 16,
              mt: 2,
              pb: !FunnelHideInnerBackground ? 2 : 0,
            }}
            align="center"
          >
            <GuaranteeIcon fontSize="large" sx={{ verticalAlign: '-0.5rem' }} />{' '}
            {FunnelFootNote || 'Our promise: no credit score impact'}
          </Typography>
        </Box>
      </Box>
    </CustomBackground>
  );
}
